import { createTheme } from "@mui/material/styles";
import type { BrandingConfig } from "../types";
import { brandingDebug } from "./brandingDebug";
import { getDialogThemeOverrides } from "./dialogThemeOverrides";
import { getDatePickerThemeOverrides } from "./datePickerThemeOverrides";
import { getButtonThemeOverrides } from "./buttonThemeOverrides";

export const APP_FONT_FAMILY =
  '"Nunito Sans", "Open Sans", "IBM Plex Sans", "Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export function createBrandingTheme(config: BrandingConfig) {
  const { themeAndColors } = config;
  const fontFamily = config.extra?.fontFamily || APP_FONT_FAMILY;

  const baseTheme = createTheme({
    typography: {
      fontFamily,
      caption: { fontSize: "0.75rem" },
      body2: { fontSize: "0.875rem" },
      subtitle1: { fontSize: "1rem" },
      h5: { fontSize: "1.25rem" },
    },
    palette: {
      primary: {
        main: themeAndColors.primaryColor,
        contrastText: themeAndColors.primaryFontColor,
      },
      secondary: {
        main: themeAndColors.secondaryColor,
      },
      text: {
        secondary: themeAndColors.secondaryFontColor,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor:
              themeAndColors.headerBackgroundColor ??
              themeAndColors.primaryColor,
          },
        },
      },
    },
  });

  const theme = createTheme(baseTheme, {
    components: {
      ...getDialogThemeOverrides(baseTheme),
      ...getDatePickerThemeOverrides(baseTheme),
      ...getButtonThemeOverrides(baseTheme),
    },
  });

  brandingDebug("createBrandingTheme", {
    inputPrimaryColor: themeAndColors.primaryColor,
    inputHeaderBackground: themeAndColors.headerBackgroundColor,
    muiPrimaryMain: theme.palette.primary.main,
    muiPrimaryContrastText: theme.palette.primary.contrastText,
    muiAppBarBackground:
      theme.components?.MuiAppBar?.styleOverrides?.root &&
      typeof theme.components.MuiAppBar.styleOverrides.root === "object" &&
      "backgroundColor" in theme.components.MuiAppBar.styleOverrides.root
        ? (theme.components.MuiAppBar.styleOverrides.root as { backgroundColor?: string })
            .backgroundColor
        : undefined,
  });

  return theme;
}
